import {
  type RequisitionHistoryEvent,
  requisitionHistory,
  responseSummary,
  type SupplierResponseHistoryEvent,
} from '@felix/domain';
import {
  IconArrowBackUp,
  IconBuilding,
  IconBuildingStore,
  IconCheck,
  IconPencil,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import classes from './Purchasing.module.css';
import { formatAmount, formatQuantity } from './requisitions.js';
import type { RequisitionDetail } from './types.js';

/**
 * Talebin tek zaman çizelgesi (04.11 notları D3/D6; sahip kararı 2026-11-05; ADR 0167): talep,
 * onay, iade, iptal, ofis revizyonları (Rev. 1, Rev. 2) ve tedarikçi yanıtları. Gemide tedarikçi
 * "Tedarikçi 1, 2 …" sırasıyla, fiyatsız görünür (veri öyle iner); ofiste adı ve teklif tutarıyla.
 * Eski sunucu çizelgeyi göndermez: onay olayları ve son ofis revizyonundan kurulur.
 */
export function historyOf(detail: RequisitionDetail): RequisitionHistoryEvent[] {
  if (detail.history) return detail.history;
  const events: RequisitionHistoryEvent[] = detail.timeline.map((event) => ({
    kind: event.kind,
    at: event.at,
    revision: event.revision,
    step: event.step,
    byName: event.byName,
    role: event.role,
    office: event.office,
    reason: event.reason,
  }));
  if (detail.revision) {
    events.push({
      kind: 'office_revision',
      at: detail.revision.createdAt,
      revisionNo: detail.revision.revisionNo,
      reason: detail.revision.reason,
      byName: detail.revision.byName,
      changed: 0,
      removed: 0,
      added: 0,
    });
  }
  return requisitionHistory(events);
}

const TONE: Record<RequisitionHistoryEvent['kind'], StatusTone> = {
  submitted: 'info',
  approved: 'success',
  returned: 'warning',
  cancelled: 'danger',
  office_revision: 'warning',
  supplier_response: 'info',
};

function EventIcon({ event }: { event: RequisitionHistoryEvent }) {
  switch (event.kind) {
    case 'submitted':
      return <IconSend size={16} />;
    case 'approved':
      return <IconCheck size={16} />;
    case 'returned':
      return <IconArrowBackUp size={16} />;
    case 'cancelled':
      return <IconX size={16} />;
    case 'office_revision':
      return <IconPencil size={16} />;
    case 'supplier_response':
      return <IconBuildingStore size={16} />;
  }
}

export function RequisitionHistory({
  detail,
  stamp,
  ship,
}: {
  detail: RequisitionDetail;
  stamp: (at: string | null) => string;
  /** Gemide: tedarikçi adı ve fiyatın gemide görünmediği notu. */
  ship: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.history.${key}`, options);
  const events = historyOf(detail);
  if (events.length === 0) return <EmptyState title={k('empty')} />;
  const hasResponses = events.some((event) => event.kind === 'supplier_response');
  return (
    <section aria-label={k('title')}>
      {ship && hasResponses ? (
        <p className={classes.notice} data-tone="info">
          {k('hidden')}
        </p>
      ) : null}
      <ol className={classes.comments} data-testid="requisition-history">
        {events.map((event, index) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: çizelge sabit sıralı, satır kimliği yok
            key={`${event.kind}-${event.at}-${index}`}
            className={classes.comment}
            data-kind={event.kind}
          >
            <span className={classes.avatar} aria-hidden>
              <EventIcon event={event} />
            </span>
            <div className={classes.historyBody}>
              <p className={classes.commentRole}>
                <StatusBadge tone={TONE[event.kind]}>{k(`kinds.${event.kind}`)}</StatusBadge>{' '}
                <EventTitle event={event} k={k} />
              </p>
              <EventBody event={event} k={k} />
            </div>
            <span className={classes.commentAt}>{stamp(event.at)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

type K = (key: string, options?: Record<string, unknown>) => string;

function EventTitle({ event, k }: { event: RequisitionHistoryEvent; k: K }) {
  switch (event.kind) {
    case 'office_revision':
      return (
        <>
          <strong className={classes.revisionNo}>
            {k('revisionBadge', { no: event.revisionNo })}
          </strong>
          {event.byName ? ` · ${event.byName}` : ''}
          <span className={classes.officeTag}>
            <IconBuilding size={14} aria-hidden /> {k('office')}
          </span>
        </>
      );
    case 'supplier_response':
      return (
        <>
          {event.supplierName ?? k('supplierNo', { no: event.responseNo })} ·{' '}
          <strong className={classes.revisionNo}>
            {k('revisionBadge', { no: event.revisionNo })}
          </strong>{' '}
          <StatusBadge tone="neutral">{k(`sources.${event.source}`)}</StatusBadge>
        </>
      );
    default:
      return (
        <>
          {event.role ?? event.byName}
          {event.role && event.byName ? ` · ${event.byName}` : ''}
          {event.office ? (
            <span className={classes.officeTag}>
              <IconBuilding size={14} aria-hidden /> {k('office')}
            </span>
          ) : null}
        </>
      );
  }
}

function EventBody({ event, k }: { event: RequisitionHistoryEvent; k: K }) {
  if (event.kind === 'office_revision') {
    return (
      <>
        <p className={classes.commentText}>{event.reason}</p>
        {event.changed + event.removed + event.added > 0 ? (
          <p className={classes.historyMeta}>
            {k('revisionChanges', {
              changed: event.changed,
              removed: event.removed,
              added: event.added,
            })}
          </p>
        ) : null}
      </>
    );
  }
  if (event.kind === 'supplier_response') return <ResponseBody event={event} k={k} />;
  return event.reason ? <p className={classes.commentText}>{event.reason}</p> : null;
}

function ResponseBody({ event, k }: { event: SupplierResponseHistoryEvent; k: K }) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const summary = responseSummary(event.lines);
  const parts = (Object.keys(summary) as (keyof typeof summary)[])
    .filter((key) => summary[key] > 0)
    .map((key) => k(`summary.${key}`, { count: summary[key] }));
  return (
    <>
      <p className={classes.historyMeta}>
        {parts.join(' · ')}
        {event.amount
          ? ` · ${k('amount', { amount: formatAmount(event.amount.value, locale), currency: event.amount.currency })}`
          : ''}
      </p>
      {event.lines.length > 0 ? (
        <details className={classes.historyLines}>
          <summary>{k('lines')}</summary>
          <div className={table.tableWrap}>
            <table className={`${table.table} ${classes.table}`}>
              <thead>
                <tr>
                  <th scope="col" className={classes.index}>
                    {k('columns.no')}
                  </th>
                  <th scope="col">{k('columns.item')}</th>
                  <th scope="col" data-numeric>
                    {k('columns.qty')}
                  </th>
                  <th scope="col">{k('columns.uom')}</th>
                  <th scope="col">{k('columns.response')}</th>
                </tr>
              </thead>
              <tbody>
                {event.lines.map((line) => (
                  <tr key={line.lineNo}>
                    <td className={classes.index}>{line.lineNo}</td>
                    <td className={classes.material}>
                      {line.name}
                      {line.substituteName ? (
                        <small className={classes.pasteNote}>
                          {k('substituteFor', { name: line.substituteName })}
                        </small>
                      ) : null}
                    </td>
                    <td data-numeric>
                      {line.quantity === null ? '—' : formatQuantity(line.quantity, locale)}
                    </td>
                    <td>{line.substituteUnit ?? line.unit}</td>
                    <td>
                      <StatusBadge
                        tone={
                          line.response === 'declined'
                            ? 'danger'
                            : line.response === 'substitute'
                              ? 'warning'
                              : line.response === 'no_price'
                                ? 'neutral'
                                : 'success'
                        }
                      >
                        {k(`responses.${line.response}`)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </>
  );
}
